// Slabs.js
import React, { useState, useEffect, useRef } from 'react';
import './Slabs.css';

// Import slab images - update paths according to your folder structure
import slab1 from '../Assets/slab1.jpg';
import slab2 from '../Assets/slab2.jpg';
import slab3 from '../Assets/slab3.jpg';
import slab4 from '../Assets/slab4.jpeg';

const Slabs = ({ language = 'en', setCurrentPage }) => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState('specifications');
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const productsRef = useRef(null);

  // SEO-optimized product data for slabs
  const products = [
    {
      id: 1,
      name: 'Quartzite Borghese Slabs',
      image: slab1,
      mine: 'Raisen',
      port: 'Xiamen Port',
      seoTitle: 'Premium Quartzite Borghese Polished Slabs | Luxury Natural Stone from India',
      metaDescription: 'Quartzite Borghese slabs from Raisen mine, India. Premium beige quartzite slabs with elegant veining, perfect for countertops and wall cladding. Available in multiple thicknesses.',
      keywords: ['quartzite borghese slabs', 'beige quartzite slabs', 'natural stone slabs', 'polished quartzite', 'indian quartzite slabs'],
      description: 'Quartzite Borghese slabs showcase the pinnacle of natural elegance with their sophisticated beige base and subtle gray veining. Each slab is masterfully processed from our premium blocks, offering consistent quality and stunning visual appeal for luxury interiors.',
      features: [
        'High-gloss polished finish with mirror-like reflection',
        'Available in 20mm and 30mm thickness options',
        'Bookmatched pairs available for feature walls',
        'Consistent beige coloration throughout each slab',
        'Pre-sealed surface for enhanced stain resistance'
      ],
      applications: [
        'Luxury kitchen countertops and islands',
        'Feature walls in hospitality projects',
        'Bathroom vanity tops and shower surrounds',
        'Commercial reception desks and lobbies',
        'High-end residential flooring'
      ],
      technicalSpecs: {
        'Standard Sizes': '280cm x 180cm, 300cm x 200cm',
        'Thickness Options': '18mm, 20mm',
        'Surface Finish': 'Polished, Honed, Leather',
        'Water Absorption': '< 1.6%',
        'Flexural Strength': '13.5 MPa',
        'Slip Resistance': 'R9-R11 (depending on finish)'
      }
    },
    {
      id: 2,
      name: 'Norwegian Wood Grain Slabs',
      image: slab2,
      mine: 'Raisen',
      port: 'Xiamen Port',
      seoTitle: 'Norwegian Wood Grain Quartzite Slabs | Wood Pattern Natural Stone Slabs',
      metaDescription: 'Norwegian Wood Grain quartzite slabs with stunning natural wood patterns. Premium polished slabs ideal for modern interiors. Ships worldwide from Xiamen port.',
      keywords: ['norwegian wood grain slabs', 'wood pattern quartzite', 'wood look stone slabs', 'linear grain quartzite', 'scandinavian stone'],
      description: 'Norwegian Wood Grain slabs capture the organic beauty of Scandinavian forests in eternal stone. These remarkable slabs feature distinctive linear patterns that mirror fine wood grains, offering unmatched durability with natural warmth.',
      features: [
        'Authentic wood grain patterns in natural stone',
        'Superior hardness exceeding granite specifications',
        'UV resistant - no fading or color change',
        'Suitable for both interior and exterior use',
        'Each slab unique with natural pattern variation'
      ],
      applications: [
        'Modern office spaces and corporate interiors',
        'Luxury yacht and marine applications',
        'Contemporary residential feature walls',
        'High-end retail store displays',
        'Spa and wellness center designs'
      ],
      technicalSpecs: {
        'Standard Sizes': '280cm x 180cm, 300cm x 200cm',
        'Thickness Options': '20mm, 30mm, 40mm',
        'Surface Finish': 'Polished, Matte, Brushed',
        'Density': '2485 kg/m³',
        'Compressive Strength': '102.5 MPa',
        'Pattern Direction': 'Consistent linear flow'
      }
    },
    {
      id: 3,
      name: 'Brazilian Wood Grain Slabs',
      image: slab3,
      mine: 'Raisen',
      port: 'Xiamen Port',
      seoTitle: 'Brazilian Wood Grain Quartzite Slabs | Exotic Wood Pattern Stone Slabs',
      metaDescription: 'Brazilian Wood Grain quartzite slabs featuring exotic natural patterns. Premium quality slabs with tropical wood appearance. Global shipping available.',
      keywords: ['brazilian wood grain slabs', 'exotic quartzite slabs', 'tropical wood pattern', 'golden brown quartzite', 'brazilian stone slabs'],
      description: 'Brazilian Wood Grain slabs embody the exotic beauty of tropical hardwoods frozen in time. These extraordinary slabs feature rich golden-brown tones with dramatic grain patterns reminiscent of the finest Brazilian timber.',
      features: [
        'Rich golden-brown base with intricate patterns',
        'Exceptional strength and durability',
        'Natural UV and heat resistance',
        'Acid-resistant for versatile applications',
        'Sustainable alternative to tropical hardwoods'
      ],
      applications: [
        'Luxury resort and hotel projects',
        'Museum and gallery installations',
        'Premium residential estates',
        'Restaurant and bar feature elements',
        'Outdoor pavilions and poolside features'
      ],
      technicalSpecs: {
        'Standard Sizes': '280cm x 180cm, 320cm x 200cm',
        'Thickness Options': '20mm, 30mm',
        'Surface Finish': 'Polished, Leather, Flamed',
        'Water Absorption': '< 1.3%',
        'Abrasion Resistance': 'Excellent (< 18mm³)',
        'Color Variation': 'Medium to High'
      }
    },
    {
      id: 4,
      name: 'Teak Wood Quartzite Slabs',
      image: slab4,
      mine: 'Raisen',
      port: 'Xiamen Port',
      seoTitle: 'Teak Wood Quartzite Slabs | Premium Teak Pattern Natural Stone Slabs',
      metaDescription: 'Teak Wood Quartzite slabs mimicking genuine teak wood. Luxury polished slabs perfect for high-end projects. Direct from Raisen mine, ships from Xiamen.',
      keywords: ['teak wood quartzite slabs', 'teak pattern stone', 'teak look slabs', 'wood grain stone slabs', 'marine grade quartzite'],
      description: 'Teak Wood Quartzite slabs masterfully replicate the prestigious appearance of teak timber in an eternal stone format. These premium slabs feature the characteristic straight grain and golden-brown hues of teak with unmatched durability.',
      features: [
        'Authentic teak wood appearance in stone',
        'Marine-grade durability for coastal projects',
        'Zero maintenance compared to real teak',
        'Maintains beauty without treatment',
        'Environmentally sustainable teak alternative'
      ],
      applications: [
        'Luxury marine and coastal architecture',
        'Five-star hotel suites and lobbies',
        'Executive office interiors',
        'High-end residential developments',
        'Outdoor kitchen countertops'
      ],
      technicalSpecs: {
        'Standard Sizes': '280cm x 180cm, 290cm x 190cm',
        'Thickness Options': '20mm, 30mm',
        'Surface Finish': 'Polished, Honed, Sandblasted',
        'Thermal Stability': 'Excellent',
        'Slip Resistance': 'R10-R12 (exterior safe)',
        'Edge Profiles': 'Multiple options available'
      }
    }
  ];

  const translations = {
    en: {
      pageTitle: 'SLABS',
      subtitle: 'Premium Quartzite Slabs',
      description: 'Discover our exclusive collection of polished quartzite slabs, precision-cut and finished to perfection. Each slab showcases the natural beauty of wood grain patterns frozen in stone, ideal for countertops, feature walls, and luxury architectural applications.',
      overview: 'Product Overview',
      specifications: 'Technical Specifications',
      applications: 'Applications',
      features: 'Key Features',
      mine: 'Mine Location',
      shipping: 'Shipping Port',
      availability: 'Availability',
      inStock: 'In Stock - Ready to Ship',
      contactForPrice: 'Contact for Pricing',
      requestQuote: 'Request Quote',
      viewDetails: 'View Full Details',
      whyChoose: 'Why Choose Our Quartzite Slabs?',
      directSource: 'Factory Direct',
      directSourceDesc: 'Direct from our processing facility with state-of-the-art Italian machinery for perfect finishes.',
      qualityAssurance: 'Premium Quality',
      qualityDesc: 'Each slab inspected for color consistency, pattern flow, and surface perfection.',
      globalShipping: 'Worldwide Delivery',
      globalDesc: 'Secure packaging and global logistics network ensuring safe delivery to your project.',
      customization: 'Custom Processing',
      customizationDesc: 'Available in multiple thicknesses, finishes, and custom sizes to meet project specifications.',
      sustainablePractice: 'Sustainable Excellence',
      sustainableDesc: 'Eco-friendly processing with water recycling and minimal waste generation.',
      seoContent: {
        h1: 'Premium Natural Quartzite Slabs with Wood Grain Patterns',
        intro: 'Experience the perfect fusion of natural beauty and modern processing with our premium quartzite slabs. Each slab is carefully selected and processed from the finest blocks, offering stunning wood grain patterns that bring warmth and elegance to any space.'
      }
    },
    zh: {
      pageTitle: '板材',
      subtitle: '优质石英岩板材',
      description: '探索我们独家的抛光石英岩板材系列，精密切割，完美加工。每块板材都展现了冻结在石头中的天然木纹图案之美，非常适合台面、特色墙和豪华建筑应用。',
      overview: '产品概述',
      specifications: '技术规格',
      applications: '应用领域',
      features: '主要特点',
      mine: '矿山位置',
      shipping: '装运港口',
      availability: '库存状态',
      inStock: '现货 - 可立即发货',
      contactForPrice: '联系询价',
      requestQuote: '索取报价',
      viewDetails: '查看详情',
      whyChoose: '为什么选择我们的石英岩板材？',
      directSource: '工厂直供',
      directSourceDesc: '直接来自我们的加工厂，配备最先进的意大利机械，确保完美的表面处理。',
      qualityAssurance: '优质品质',
      qualityDesc: '每块板材都经过颜色一致性、图案流动和表面完美度检验。',
      globalShipping: '全球配送',
      globalDesc: '安全包装和全球物流网络，确保安全送达您的项目现场。',
      customization: '定制加工',
      customizationDesc: '提供多种厚度、表面处理和定制尺寸，以满足项目规格要求。',
      sustainablePractice: '可持续卓越',
      sustainableDesc: '环保加工，水循环利用，废料产生最少。',
      seoContent: {
        h1: '优质天然木纹石英岩板材',
        intro: '体验自然美与现代加工的完美融合。每块板材都是从最优质的荒料中精心挑选和加工而成，展现迷人的木纹图案，为任何空间带来温暖和优雅。'
      }
    },
    it: {
      pageTitle: 'LASTRE',
      subtitle: 'Lastre di Quarzite Premium',
      description: 'Scopri la nostra collezione esclusiva di lastre di quarzite lucidate, tagliate con precisione e rifinite alla perfezione. Ogni lastra mostra la bellezza naturale dei motivi a venature di legno congelati nella pietra, ideali per piani di lavoro, pareti caratteristiche e applicazioni architettoniche di lusso.',
      overview: 'Panoramica Prodotto',
      specifications: 'Specifiche Tecniche',
      applications: 'Applicazioni',
      features: 'Caratteristiche Principali',
      mine: 'Ubicazione Miniera',
      shipping: 'Porto di Spedizione',
      availability: 'Disponibilità',
      inStock: 'Disponibile - Pronto per la Spedizione',
      contactForPrice: 'Contatta per Prezzi',
      requestQuote: 'Richiedi Preventivo',
      viewDetails: 'Vedi Dettagli Completi',
      whyChoose: 'Perché Scegliere le Nostre Lastre di Quarzite?',
      directSource: 'Diretto dalla Fabbrica',
      directSourceDesc: 'Direttamente dal nostro stabilimento con macchinari italiani all\'avanguardia per finiture perfette.',
      qualityAssurance: 'Qualità Premium',
      qualityDesc: 'Ogni lastra ispezionata per consistenza del colore, flusso del pattern e perfezione della superficie.',
      globalShipping: 'Consegna Mondiale',
      globalDesc: 'Imballaggio sicuro e rete logistica globale per garantire consegna sicura al vostro progetto.',
      customization: 'Lavorazione Personalizzata',
      customizationDesc: 'Disponibile in diversi spessori, finiture e dimensioni personalizzate per soddisfare le specifiche del progetto.',
      sustainablePractice: 'Eccellenza Sostenibile',
      sustainableDesc: 'Lavorazione eco-compatibile con riciclo dell\'acqua e minima generazione di rifiuti.',
      seoContent: {
        h1: 'Lastre Premium di Quarzite Naturale con Venature Effetto Legno',
        intro: 'Scopri la perfetta fusione di bellezza naturale e lavorazione moderna con le nostre lastre di quarzite premium. Ogni lastra è accuratamente selezionata e lavorata dai migliori blocchi, offrendo splendidi motivi a venature di legno che portano calore ed eleganza in qualsiasi spazio.'
      }
    }
  };

  const t = translations[language] || translations.en;

  useEffect(() => {
    setIsLoaded(true);

    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 2;
      const y = (e.clientY / window.innerHeight - 0.5) * 2;
      setMousePosition({ x, y });
    };

    window.addEventListener('mousemove', handleMouseMove);

    // Intersection Observer for animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
          }
        });
      },
      { threshold: 0.1 }
    );

    const animatedElements = document.querySelectorAll('.animate-on-scroll');
    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      observer.disconnect();
    };
  }, []);


  const handleContactClick = () => {
    setCurrentPage('contact');
  };

  return (
    <div className={`slabs-container ${isLoaded ? 'loaded' : ''}`}>
      {/* Hero Section */}
      <section className="slabs-hero">
        <div className="hero-background">
          <div className="parallax-layer layer-1" />
        </div>
        <div className="hero-content1">
          <h1 className="hero-title">{t.pageTitle}</h1>
          <p className="hero-subtitle">{t.subtitle}</p>
          <p className="hero-description">{t.description}</p>
        </div>
      </section>

      {/* Products Grid */}
      <section className="products-section" ref={productsRef}>
        <div className="section-container">
          <div className="section-header">
            <span className="section-label">OUR COLLECTION</span>
            <h2 className="section-title1">{t.seoContent.h1}</h2>
            <p className="section-subtitle">{t.seoContent.intro}</p>
          </div>

          <div className="products-grid">
            {products.map((product, index) => (
              <article 
                key={product.id} 
                className="product-card animate-on-scroll"
                onClick={() => setSelectedProduct(index)}
              >
                <div className="product-image-wrapper">
                  <img src={product.image} alt={`${product.name} - Premium Quartzite Slabs from ${product.mine} Mine`} />
                  <div className="product-overlay">
                    <button className="view-btn">{t.viewDetails}</button>
                  </div>
                </div>
                <div className="product-info">
                  <h3 className="product-name">{product.name}</h3>
                  <div className="product-meta">
                    <span className="meta-item">
                      <svg viewBox="0 0 24 24" width="16" height="16">
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="currentColor"/>
                      </svg>
                      {t.mine}: {product.mine}
                    </span>
                    <span className="meta-item">
                      <svg viewBox="0 0 24 24" width="16" height="16">
                        <path d="M21 16.5c0 .38-.21.71-.53.88l-7 4c-.15.08-.31.12-.47.12s-.32-.04-.47-.12l-7-4A1 1 0 015 16.5v-9c0-.38.21-.71.53-.88l7-4c.3-.16.64-.16.94 0l7 4c.32.17.53.5.53.88v9z" fill="currentColor"/>
                      </svg>
                      {t.shipping}: {product.port}
                    </span>
                  </div>
                  <div className="product-availability">
                    <span className="availability-badge">{t.inStock}</span>
                  </div>
                  <button className="contact-btn" onClick={(e) => {
                    e.stopPropagation();
                    handleContactClick();
                  }}>
                    {t.contactForPrice}
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="why-choose-section animate-on-scroll">
        <div className="section-container">
          <h2 className="section-title">{t.whyChoose}</h2>
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon">
                <svg viewBox="0 0 24 24" width="40" height="40">
                  <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" fill="currentColor"/>
                </svg>
              </div>
              <h3>{t.directSource}</h3>
              <p>{t.directSourceDesc}</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">
                <svg viewBox="0 0 24 24" width="40" height="40">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="currentColor"/>
                </svg>
              </div>
              <h3>{t.qualityAssurance}</h3>
              <p>{t.qualityDesc}</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">
                <svg viewBox="0 0 24 24" width="40" height="40" fill="currentColor">
                  <path d="M3 3h13v13H3V3zm14 0h4l2 4v9h-6V3zm-1 14c0 1.1.9 2 2 2s2-.9 2-2-.9-2-2-2-2 .9-2 2zm-10 0c0 1.1.9 2 2 2s2-.9 2-2-.9-2-2-2-2 .9-2 2zM5 5v9h9V5H5zm14 4l-1.5-3H17v3h2z"/>
                </svg>
              </div>
              <h3>{t.globalShipping}</h3>
              <p>{t.globalDesc}</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">
                <svg viewBox="0 0 24 24" width="40" height="40">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="currentColor"/>
                </svg>
              </div>
              <h3>{t.customization}</h3>
              <p>{t.customizationDesc}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Product Detail Modal */}
      {selectedProduct !== null && (
        <div className="product-modal" onClick={() => setSelectedProduct(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedProduct(null)}>
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>

            <div className="modal-grid">
              <div className="modal-image">
                <img src={products[selectedProduct].image} alt={products[selectedProduct].name} />
              </div>

              <div className="modal-details">
                <h2 className="modal-title">{products[selectedProduct].name}</h2>
                <p className="modal-description">{products[selectedProduct].description}</p>

                <div className="modal-tabs">
                  <button 
                    className={`tab-btn ${activeTab === 'specifications' ? 'active' : ''}`}
                    onClick={() => setActiveTab('specifications')}
                  >
                    {t.specifications}
                  </button>
                  <button 
                    className={`tab-btn ${activeTab === 'features' ? 'active' : ''}`}
                    onClick={() => setActiveTab('features')}
                  >
                    {t.features}
                  </button>
                  <button 
                    className={`tab-btn ${activeTab === 'applications' ? 'active' : ''}`}
                    onClick={() => setActiveTab('applications')}
                  >
                    {t.applications}
                  </button>
                </div>

                <div className="tab-content">
                  {activeTab === 'specifications' && (
                    <div className="specs-list">
                      {Object.entries(products[selectedProduct].technicalSpecs).map(([key, value]) => (
                        <div key={key} className="spec-item">
                          <span className="spec-key">{key}:</span>
                          <span className="spec-value">{value}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {activeTab === 'features' && (
                    <ul className="features-list">
                      {products[selectedProduct].features.map((feature, index) => (
                        <li key={index}>{feature}</li>
                      ))}
                    </ul>
                  )}

                  {activeTab === 'applications' && (
                    <ul className="applications-list">
                      {products[selectedProduct].applications.map((application, index) => (
                        <li key={index}>{application}</li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="modal-actions">
                  <button className="primary-btn" onClick={handleContactClick}>{t.requestQuote}</button>
                  <div className="shipping-info">
                    <p><strong>{t.mine}:</strong> {products[selectedProduct].mine}</p>
                    <p><strong>{t.shipping}:</strong> {products[selectedProduct].port}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SEO Content Section */}
      <section className="seo-content-section">
        <div className="section-container">
          <div className="seo-content">
            <h3>Premium Quartzite Slabs - The Perfect Wood Look Alternative</h3>
            <p>
              Our premium quartzite slabs represent the ultimate evolution in natural stone processing. Each slab, 
              whether Norwegian Wood Grain, Brazilian Wood Grain, Teak Wood Quartzite, or classic Quartzite Borghese, 
              is expertly processed using state-of-the-art Italian machinery to achieve perfect surface finishes and 
              consistent thickness throughout.
            </p>
            
            <h3>Why Wood Grain Quartzite Slabs Excel in Modern Design</h3>
            <p>
              Wood grain quartzite slabs offer the warm, organic aesthetic of natural wood with the unparalleled 
              durability of quartzite. Unlike traditional wood or even engineered materials, our slabs are completely 
              waterproof, heat-resistant, and scratch-proof, making them ideal for both residential and commercial 
              applications where beauty meets functionality.
            </p>

            <h3>Processing Excellence from Block to Slab</h3>
            <p>
              Our advanced processing facility transforms raw blocks into perfect slabs using precision diamond wire 
              saws and multi-head polishing machines. Each slab undergoes rigorous quality control, ensuring consistent 
              thickness, flawless surface finish, and optimal pattern presentation. Available in polished, honed, and 
              leather finishes to suit any design vision.
            </p>

            <h3>Technical Superiority for Demanding Applications</h3>
            <p>
              With water absorption below 1.6% and flexural strength exceeding 13 MPa, our quartzite slabs surpass 
              industry standards for both interior and exterior use. The natural quartzite composition ensures 
              excellent thermal stability, making these slabs perfect for kitchen countertops, outdoor kitchens, 
              and facade cladding in all climate conditions.
            </p>

            <h3>Inventory and Custom Processing</h3>
            <p>
              We maintain extensive inventory of standard sizes (280cm x 180cm and 300cm x 200cm) in multiple 
              thicknesses. Custom sizing, edge profiles, and surface finishes are available for projects requiring 
              specific specifications. Our bookmatching service creates stunning symmetrical patterns for feature 
              walls and statement pieces.
            </p>
          </div>
        </div>
      </section>

      {/* Floating Background Elements */}
      <div className="background-shapes">
        <div 
          className="shape shape-1"
          style={{ transform: `translate(${mousePosition.x * 20}px, ${mousePosition.y * 20}px)` }}
        />
        <div 
          className="shape shape-2"
          style={{ transform: `translate(${mousePosition.x * -15}px, ${mousePosition.y * -15}px)` }}
        />
      </div>
    </div>
  );
};

export default Slabs;